import { Stack, Typography } from "@mui/material";
import { CSSProperties, useEffect, useState } from "react";
import { ReactSortable, SortableEvent } from "react-sortablejs";
import { toast } from "react-toastify";
import {
  useDeleteKeyTitleMutation,
  useUpdateKeyTitleMutation,
} from "../../../api";
import {
  ExtendedTitle,
  KeyRole,
  UpdateKeyTitle,
} from "../../../types/apiTypes";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { CustomToast } from "../../ui/CustomToast";
import { KeyTitlesItem } from "./KeyTitlesItem";
import { ReuseTooltip } from "../../ui";
import { QuestionMarkInCircleIcon } from "../../images/QuestionMarkInCircleIcon";
import { KeyTitleAddItemBlock } from "./KeyTitleAddItemBlock";
import { JobTitlesType } from "../../../types/enums";

type Props = {
  role: KeyRole;
  campaign_id: string;
  isEditor: boolean;
};

const style: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "8px",
  flexWrap: "wrap",
};

const sortableOptions = {
  animation: 150,
  fallbackOnBody: true,
  swapThreshold: 0.65,
  ghostClass: "ghost",
  forceFallback: true,
  style,
};

const approvedOptions = {
  ...sortableOptions,
  group: { name: "shared", pull: true, put: true },
  filter: ".no-drag, input",
};

const suggestedOptions = {
  ...sortableOptions,
  group: { name: "shared", pull: true, put: false },
  filter: ".no-drag, input",
};

const ignoredOptions = {
  ...sortableOptions,
  group: { name: "shared", pull: true, put: true },
  filter: ".no-drag, input",
};

export const KeyTitlesSortableList = ({
  role,
  campaign_id,
  isEditor,
}: Props) => {
  const [approved, setApprovedTitles] = useState<ExtendedTitle[]>([]);
  const [suggested, setSuggestedTitles] = useState<ExtendedTitle[]>([]);
  const [ignored, setIgnoredTitles] = useState<ExtendedTitle[]>([]);
  const [selectedTitleId, setSelectedTitleId] = useState("");

  const [
    updateKeyTitle,
    {
      error: updateError,
      isError: isUpdateError,
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateKeyTitleMutation();

  const [
    deleteKeyTitle,
    {
      error: deleteError,
      isError: isDeleteError,
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteKeyTitleMutation();

  const lengthMapper: Record<string, number> = {
    approved: approved.length,
    suggested: suggested.length,
    ignored: ignored.length,
  };

  const onAdd = (e: SortableEvent) => {
    const id = e.item.getAttribute("data-id");
    const [type, roleId] = e.to.getAttribute("id")?.split(";") || [
      undefined,
      undefined,
    ];

    if (!id || e.newIndex === undefined || !roleId || !type) return;

    updateKeyTitle({
      id,
      campaign_id,
      position:
        e.newIndex > lengthMapper[type] ? lengthMapper[type] : e.newIndex,
      is_approved: true,
      is_ignored: type === JobTitlesType.ignored,
      role_id: roleId,
    });
  };

  const onUpdate = (e: SortableEvent) => {
    const id = e.item.getAttribute("data-id");
    const [type] = e.to.getAttribute("id")?.split(";") || [undefined];

    if (!id || e.newIndex === e.oldIndex || e.newIndex === undefined || !type)
      return;
    updateKeyTitle({
      id,
      campaign_id,
      position:
        e.newIndex > lengthMapper[type] - 1
          ? lengthMapper[type] - 1
          : e.newIndex,
    });
  };

  const changeHandler = (data: Omit<UpdateKeyTitle, "campaign_id">) => {
    setSelectedTitleId(data.id);
    updateKeyTitle({ campaign_id, ...data });
  };

  const removeHandler = (key_id: string) => {
    setSelectedTitleId(key_id);
    deleteKeyTitle({ campaign_id, key_id });
  };

  useEffect(() => {
    setApprovedTitles(role.approved_titles || []);
    setSuggestedTitles(role.suggested_titles || []);
    setIgnoredTitles(role.ignored_titles || []);
  }, [role]);

  useEffect(() => {
    if (isUpdateError && updateError) {
      setSelectedTitleId("");

      toast.error(
        <CustomToast
          type="error"
          text={
            getErrorMessage(updateError).globalError ||
            "Failed to update job title"
          }
          title="Update failed"
        />,
      );
    }
  }, [isUpdateError]);

  useEffect(() => {
    setSelectedTitleId("");

    if (isDeleteError && deleteError) {
      toast.error(
        <CustomToast
          type="error"
          text={
            getErrorMessage(deleteError).globalError ||
            "Failed to delete job title"
          }
          title="Delete failed"
        />,
      );
    }
  }, [isDeleteError]);

  useEffect(() => {
    if (isDeleteSuccess || isUpdateSuccess) {
      setSelectedTitleId("");
    }
  }, [isDeleteSuccess, isUpdateSuccess]);

  if (!isEditor) {
    return (
      <Stack gap="16px">
        {approved.length > 0 && (
          <Stack gap="8px">
            <Typography
              variant="body1"
              color={"custom.textDarkBlue"}
              fontWeight={500}
            >
              Target Job Titles
            </Typography>
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {approved.map(({ name, id }) => (
                <Stack
                  key={id}
                  sx={{
                    padding: "4px 12px",
                    borderRadius: "6px",
                    backgroundColor: "#F4F5F7",
                  }}
                >
                  <Typography
                    component="span"
                    variant="body1"
                    color="custom.textDarkBlue"
                    sx={{
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}
                  >
                    {name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}

        {ignored.length > 0 && (
          <Stack gap="8px">
            <Stack gap="4px" flexDirection="row" alignItems="center">
              <Typography
                variant="body1"
                color={"custom.textDarkBlue"}
                fontWeight={500}
              >
                Ignore List
              </Typography>
              <ReuseTooltip
                maxWidth={"200px"}
                text={
                  "Suppressed titles in any role will be applied on the campaign level"
                }
              >
                <QuestionMarkInCircleIcon
                  width="20px"
                  height="20px"
                  color="#9B9B9B"
                />
              </ReuseTooltip>
            </Stack>
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {ignored.map(({ name, id }) => (
                <Stack
                  key={id}
                  sx={{
                    padding: "4px 12px",
                    borderRadius: "6px",
                    backgroundColor: "#F2D7D5",
                  }}
                >
                  <Typography
                    component="span"
                    variant="body1"
                    color="custom.textDarkBlue"
                    sx={{
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                    }}
                  >
                    {name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="16px">
      <Stack gap="8px">
        <Typography
          variant="body1"
          color={"custom.textDarkBlue"}
          fontWeight={500}
        >
          Target Job Titles
        </Typography>
        <ReactSortable
          list={approved.map((title) => ({ ...title, chosen: true }))}
          setList={setApprovedTitles}
          {...approvedOptions}
          onAdd={onAdd}
          onUpdate={onUpdate}
          preventOnFilter={false}
          id={`approved;${role.id}`}
        >
          {approved.map((title) => (
            <KeyTitlesItem
              item={title}
              key={title.id}
              ignoredLength={ignored.length}
              approvedLength={approved.length}
              changeHandler={changeHandler}
              removeHandler={removeHandler}
              loading={
                selectedTitleId === title.id &&
                (isUpdateLoading || isDeleteLoading)
              }
            />
          ))}

          <KeyTitleAddItemBlock
            titleType={JobTitlesType.approved}
            roleId={role.id}
            isReachedLimit={approved.length < 15}
          />
        </ReactSortable>
      </Stack>

      {suggested.length > 0 && (
        <Stack gap="8px">
          <Typography
            variant="body1"
            color={"custom.textDarkBlue"}
            fontWeight={500}
          >
            Suggestions
          </Typography>

          <ReactSortable
            list={suggested.map((title) => ({ ...title, chosen: true }))}
            setList={setSuggestedTitles}
            {...suggestedOptions}
            onAdd={onAdd}
            onUpdate={onUpdate}
            preventOnFilter={false}
            id={`suggested;${role.id}`}
          >
            {suggested.map((title) => (
              <KeyTitlesItem
                item={title}
                key={title.id}
                ignoredLength={ignored.length}
                approvedLength={approved.length}
                changeHandler={changeHandler}
                removeHandler={removeHandler}
                loading={
                  selectedTitleId === title.id &&
                  (isUpdateLoading || isDeleteLoading)
                }
              />
            ))}
          </ReactSortable>
        </Stack>
      )}

      <Stack gap="8px">
        <Stack gap="4px" flexDirection="row" alignItems="center">
          <Typography
            variant="body1"
            color={"custom.textDarkBlue"}
            fontWeight={500}
          >
            Ignore List
          </Typography>
          <ReuseTooltip
            maxWidth={"200px"}
            text={
              "Suppressed titles in any role will be applied on the campaign level"
            }
          >
            <QuestionMarkInCircleIcon
              width="20px"
              height="20px"
              color="#9B9B9B"
            />
          </ReuseTooltip>
        </Stack>

        <ReactSortable
          list={ignored.map((title) => ({ ...title, chosen: true }))}
          setList={setIgnoredTitles}
          {...ignoredOptions}
          onAdd={onAdd}
          onUpdate={onUpdate}
          preventOnFilter={false}
          id={`ignored;${role.id}`}
        >
          {ignored.map((title) => (
            <KeyTitlesItem
              isIgnored={true}
              item={title}
              key={title.id}
              ignoredLength={ignored.length}
              approvedLength={ignored.length}
              changeHandler={changeHandler}
              removeHandler={removeHandler}
              loading={
                selectedTitleId === title.id &&
                (isUpdateLoading || isDeleteLoading)
              }
            />
          ))}

          <KeyTitleAddItemBlock
            titleType={JobTitlesType.ignored}
            roleId={role.id}
            isReachedLimit={approved.length < 15}
          />
        </ReactSortable>
      </Stack>
    </Stack>
  );
};
